import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import api from '../../services/api';

/* ───────── animations ───────── */
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;
const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 15px rgba(218, 165, 32, 0.15); }
  50% { box-shadow: 0 0 30px rgba(218, 165, 32, 0.3); }
`;

/* ───────── layout ───────── */
const Page = styled.div`
  min-height: calc(100vh - 160px);
  background: #FFFDF7;
  padding: 2rem 1.5rem 3rem;

  @media (max-width: 600px) {
    padding: 1rem 1rem 2rem;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.6rem, 4vw, 2.5rem);
  font-weight: 800;
  margin: 0 0 0.5rem;
  color: #2C1810;
`;

const PageSub = styled.p`
  text-align: center;
  color: #9B8B7A;
  font-size: 0.95rem;
  margin: 0 0 0.5rem;
`;

const GoldDivider = styled.div`
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #DAA520, transparent);
  margin: 0.5rem auto 2.5rem;
`;

/* ───────── service cards ───────── */
const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  animation: ${slideUp} 0.8s ease-out;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(218, 165, 32, 0.1);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(.25,.8,.25,1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${p => p.$accent || '#DAA520'}, transparent);
    opacity: 0;
    transition: opacity 0.35s;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 40px rgba(139, 105, 20, 0.1);
    border-color: ${p => p.$accent || '#DAA520'}44;
    &::before { opacity: 1; }
    .svc-icon { animation: ${float} 2s ease-in-out infinite; }
  }

  @media (max-width: 600px) {
    padding: 1.2rem 0.8rem;
    border-radius: 12px;
  }
`;

const SvcIcon = styled.div.attrs({ className: 'svc-icon' })`
  font-size: 2.5rem;
  margin-bottom: 1rem;

  @media (max-width: 600px) {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
  }
`;

const SvcTitle = styled.h3`
  color: #2C1810;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 0.5rem;

  @media (max-width: 600px) {
    font-size: 0.85rem;
    margin: 0 0 0.3rem;
  }
`;

const SvcDesc = styled.p`
  color: #9B8B7A;
  font-size: 0.82rem;
  line-height: 1.5;
  margin: 0 0 0.75rem;

  @media (max-width: 600px) {
    font-size: 0.68rem;
    line-height: 1.35;
    margin: 0 0 0.5rem;
  }
`;

const SvcPrice = styled.p`
  color: #4caf50;
  font-weight: 700;
  font-size: 0.85rem;
  margin: 0;
`;

const SvcBadge = styled.span`
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  background: ${p => p.$bg || '#DAA520'};
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

/* ───────── astrologer section ───────── */
const SectionTitle = styled.h2`
  text-align: center;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.2rem, 3vw, 1.6rem);
  font-weight: 700;
  color: #2C1810;
  margin: 3rem 0 0.5rem;
`;

const SectionSub = styled.p`
  text-align: center;
  color: #9B8B7A;
  font-size: 0.85rem;
  margin: 0 0 1.5rem;
`;

const AstrologerScroll = styled.div`
  display: flex;
  gap: 1.2rem;
  overflow-x: auto;
  padding: 0.5rem 0 1rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(218, 165, 32, 0.2) transparent;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(218, 165, 32, 0.2); border-radius: 4px; }
`;

const AstroMiniCard = styled.div`
  flex: 0 0 150px;
  background: #FFFFFF;
  border: 1px solid rgba(218, 165, 32, 0.1);
  border-radius: 14px;
  padding: 1.2rem 0.8rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 20px rgba(218, 165, 32, 0.12);
    border-color: rgba(218, 165, 32, 0.3);
  }
`;

const MiniAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  margin: 0 auto 0.6rem;
  background: linear-gradient(135deg, #DAA520, #B8860B);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  animation: ${glowPulse} 3s ease-in-out infinite;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const MiniStatus = styled.span`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.$online ? '#4caf50' : '#ccc'};
  border: 2px solid #fff;
`;

const MiniName = styled.p`
  color: #2C1810;
  font-size: 0.78rem;
  font-weight: 600;
  margin: 0 0 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MiniSpec = styled.p`
  color: #9B8B7A;
  font-size: 0.65rem;
  margin: 0;
`;

const ViewAllBtn = styled.button`
  display: block;
  margin: 1.5rem auto 0;
  padding: 10px 28px;
  border-radius: 10px;
  border: 1.5px solid #DAA520;
  background: transparent;
  color: #8B6914;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s;
  font-family: 'Inter', Arial, sans-serif;

  &:hover {
    background: linear-gradient(135deg, #DAA520, #B8860B);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(218, 165, 32, 0.3);
  }
`;

/* ───────── data ───────── */
const SERVICES = [
  {
    icon: '🃏',
    title: 'Tarot Reading',
    desc: 'Unlock hidden truths with mystical tarot card readings from certified readers.',
    accent: '#9c27b0',
    badge: 'Popular',
    badgeBg: '#9c27b0',
    price: 'From ₹15/min',
  },
  {
    icon: '🤚',
    title: 'Palm Reading',
    desc: 'Discover your destiny through the ancient art of palmistry with our experts.',
    accent: '#e91e63',
    price: 'From ₹12/min',
  },
  {
    icon: '✨',
    title: 'Reiki Healing',
    desc: 'Balance your energy chakras with guided reiki sessions for mind, body & soul.',
    accent: '#00bcd4',
    price: 'From ₹20/min',
  },
  {
    icon: '🔢',
    title: 'Numerology',
    desc: 'Decode the hidden power of numbers in your name, birth date & life path.',
    accent: '#DAA520',
    price: 'From ₹10/min',
  },
  {
    icon: '🏠',
    title: 'Vastu Shastra',
    desc: 'Harmonize your living spaces for prosperity with Vastu consultations.',
    accent: '#4caf50',
    price: 'From ₹15/min',
  },
  {
    icon: '🌙',
    title: 'Vedic Astrology',
    desc: 'Deep kundli analysis, dasha predictions, and personalized remedies.',
    accent: '#B8860B',
    badge: 'Top Rated',
    badgeBg: '#B8860B',
    price: 'From ₹10/min',
  },
  {
    icon: '👁️',
    title: 'Face Reading',
    desc: 'Reveal personality traits and future through the science of physiognomy.',
    accent: '#f44336',
    price: 'From ₹12/min',
  },
  {
    icon: '📿',
    title: 'Prashna Kundli',
    desc: 'Get instant answers to your pressing questions through horary astrology.',
    accent: '#795548',
    price: 'From ₹15/min',
  },
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || '').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
};

/* ───────── component ───────── */
const MainPage = () => {
  const navigate = useNavigate();
  const [astrologers, setAstrologers] = useState([]);

  useEffect(() => {
    api.get('/astrologers')
      .then(res => {
        const data = res.data?.astrologers || res.data || [];
        const list = Array.isArray(data) ? data : [];
        setAstrologers(list.slice(0, 10));
      })
      .catch(() => {});
  }, []);

  return (
    <Page>
      <Content>
        <PageTitle>Book a Consultation</PageTitle>
        <PageSub>Choose from our spiritual services and connect with expert practitioners</PageSub>
        <GoldDivider />

        <ServiceGrid>
          {SERVICES.map((svc, i) => (
            <ServiceCard key={i} $accent={svc.accent} onClick={() => navigate('/talk-to-astrologer')}>
              {svc.badge && <SvcBadge $bg={svc.badgeBg}>{svc.badge}</SvcBadge>}
              <SvcIcon>{svc.icon}</SvcIcon>
              <SvcTitle>{svc.title}</SvcTitle>
              <SvcDesc>{svc.desc}</SvcDesc>
              <SvcPrice>{svc.price}</SvcPrice>
            </ServiceCard>
          ))}
        </ServiceGrid>

        {/* Available Astrologers */}
        {astrologers.length > 0 && (
          <>
            <SectionTitle>Available Astrologers</SectionTitle>
            <SectionSub>Connect instantly with our experts</SectionSub>
            <AstrologerScroll>
              {astrologers.map(a => {
                const name = a.name || a.user?.name || 'Astrologer';
                const isOnline = a.online_status === 'online' || a.is_online;
                return (
                  <AstroMiniCard key={a.id} onClick={() => navigate(`/astrologer/${a.id}`)}>
                    <MiniAvatar>
                      {a.profile_image ? (
                        <img src={a.profile_image} alt={name} />
                      ) : (
                        getInitials(name)
                      )}
                      <MiniStatus $online={isOnline} />
                    </MiniAvatar>
                    <MiniName>{name}</MiniName>
                    <MiniSpec>{a.specialization || 'Astrology'}</MiniSpec>
                  </AstroMiniCard>
                );
              })}
            </AstrologerScroll>
            <ViewAllBtn onClick={() => navigate('/talk-to-astrologer')}>
              View All Astrologers →
            </ViewAllBtn>
          </>
        )}
      </Content>
    </Page>
  );
};

export default MainPage;
